import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as actions from '../../../../redux/action_creators';
import CommonHeader from '../../../util_components/common_header';
import { get_api, put_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import './yoga_cues_library.css';

const BASE = '/v2/cms_user/yoga_cues';
const LOW_CONFIDENCE = 0.6;

const fmt_duration = (secs) => {
  const s = Number(secs) || 0;
  const m = Math.floor(s / 60);
  const r = String(s % 60).padStart(2, '0');
  return `${m}:${r}`;
};

const fmt_clock = (secs) => {
  const s = Number(secs) || 0;
  const h = String(Math.floor(s / 3600)).padStart(2, '0');
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const sec = String(s % 60).padStart(2, '0');
  return `${h}:${m}:${sec}`;
};

const plural = (n, word) => `${n} ${word}${Number(n) === 1 ? '' : 's'}`;

class YogaCuesLibrary extends Component {
  state = {
    loading: true,
    tab: 'all', // 'all' | 'shortlisted'
    categories: [],
    cat_id: 0,
    title_id: 0,
    min_rating: 0,
    query: '',
    segments: [],
    sort_key: 'rating', // 'rating' | 'time' | 'title'
    sort_dir: 'desc',
    expanded_id: 0,
    selected_id: 0,
    detail: null,
    active_cue: -1,
    load_error: false,
    edit_title: '',
    edit_desc: '',
    edit_notes: '',
    edit_rating: ''
  };

  video_ref = React.createRef();

  bounded_play = false; // true only while a "Play segment" click is bounded to the segment end

  cue_refs = {}; // transcript-cue elements, for auto-scroll of the active line

  componentDidMount() {
    this.load_categories();
  }

  show_error = (e) => {
    let msg;
    try {
      msg = e.response.data.reason || e.response.data.message;
    } catch (err) {
      msg = 'Some error occurred. Please contact dev team.';
    }
    this.props.set_notification_variable(true, 'error', msg);
  };

  // Categories in the current tab that have content (shortlisted tab hides empty categories).
  visible_categories = () => {
    const { categories, tab } = this.state;
    return tab === 'shortlisted' ? categories.filter((c) => c.segment_count > 0) : categories;
  };

  // preserve=true refreshes category counts without moving the user's current selection (used
  // after a shortlist toggle) — unless the selected category no longer has any rows.
  load_categories = (preserve = false) => {
    const sl = this.state.tab === 'shortlisted' ? '?shortlisted=true' : '';
    get_api(get_api_url(`${BASE}/categories${sl}`), true)
      .then((res) => {
        const categories = res.data.data || [];
        const list = this.state.tab === 'shortlisted' ? categories.filter((c) => c.segment_count > 0) : categories;
        let { cat_id, expanded_id } = this.state;
        if (!preserve || !list.some((c) => c.id === cat_id)) {
          const first = list[0] || {};
          cat_id = first.id || 0;
          expanded_id = first.id || 0;
        }
        this.setState({ categories, cat_id, expanded_id }, this.load_segments);
      })
      .catch((e) => {
        this.show_error(e);
        this.setState({ loading: false, load_error: true });
      });
  };

  load_segments = () => {
    const { cat_id, title_id, min_rating, query, tab } = this.state;
    this.setState({ loading: true, load_error: false });
    const params = new URLSearchParams();
    if (cat_id) params.append('category_id', cat_id);
    if (title_id) params.append('canonical_title_id', title_id);
    if (min_rating) params.append('min_rating', min_rating);
    if (query) params.append('q', query);
    if (tab === 'shortlisted') params.append('shortlisted', 'true');
    get_api(get_api_url(`${BASE}/segments?${params.toString()}`), true)
      .then((res) => {
        const segments = res.data.data || [];
        this.setState({ segments, loading: false });
        if (segments.length) {
          this.load_detail(segments[0].id);
        } else {
          this.setState({ detail: null, selected_id: 0 });
        }
      })
      .catch((e) => {
        this.show_error(e);
        this.setState({ loading: false, load_error: true });
      });
  };

  load_detail = (id) => {
    get_api(get_api_url(`${BASE}/segments/${id}`), true)
      .then((res) => {
        const detail = res.data.data;
        this.bounded_play = false;
        this.cue_refs = {};
        this.setState({
          detail,
          selected_id: id,
          active_cue: -1,
          edit_title: detail.title || '',
          edit_desc: detail.description || '',
          edit_notes: detail.reviewer_notes || '',
          edit_rating: detail.rating == null ? '' : String(detail.rating)
        }, () => {
          // Re-seek to the new segment's start. Segments in the same session share a video src,
          // so onLoadedMetadata won't refire — seek here so switching rows repositions the clip.
          const v = this.video_ref.current;
          if (v && detail) v.currentTime = detail.start_time_seconds || 0;
        });
      })
      .catch((e) => this.show_error(e));
  };

  // Accordion: clicking the open category collapses it; clicking any other opens + loads it.
  toggle_category = (cat) => {
    if (this.state.expanded_id === cat.id) {
      this.setState({ expanded_id: 0 });
      return;
    }
    this.setState({ expanded_id: cat.id, cat_id: cat.id, title_id: 0 }, this.load_segments);
  };

  select_title = (title_id) => this.setState({ title_id }, this.load_segments);

  switch_tab = (tab) => {
    if (tab === this.state.tab) return;
    this.setState({ tab, cat_id: 0, title_id: 0, selected_id: 0, detail: null, query: '' }, this.load_categories);
  };

  // Patch a row in the loaded table in place so edits reflect immediately without a refetch.
  patch_segment = (id, fields) => {
    this.setState((prev) => ({
      segments: prev.segments.map((s) => (s.id === id ? { ...s, ...fields } : s))
    }));
  };

  set_sort = (key) => {
    this.setState((prev) => {
      if (prev.sort_key === key) return { sort_dir: prev.sort_dir === 'asc' ? 'desc' : 'asc' };
      return { sort_key: key, sort_dir: key === 'rating' ? 'desc' : 'asc' };
    });
  };

  sort_indicator = (key) => {
    if (this.state.sort_key !== key) return '';
    return this.state.sort_dir === 'asc' ? ' ▲' : ' ▼';
  };

  // Client-side sort of the loaded rows (default: rating high→low, unrated last).
  sorted_segments = () => {
    const { segments, sort_key, sort_dir } = this.state;
    const dir = sort_dir === 'asc' ? 1 : -1;
    return [...segments].sort((a, b) => {
      if (sort_key === 'title') return dir * (a.title || '').localeCompare(b.title || '');
      if (sort_key === 'time') return dir * ((a.start_time_seconds || 0) - (b.start_time_seconds || 0));
      const ar = a.rating == null ? -1 : a.rating;
      const br = b.rating == null ? -1 : b.rating;
      return dir * (ar - br);
    });
  };

  toggle_shortlist = () => {
    const { selected_id, detail, tab } = this.state;
    if (!detail) return;
    const next = !detail.is_shortlisted;
    put_api(get_api_url(`${BASE}/segments/${selected_id}/shortlist`), { shortlisted: next }, true)
      .then(() => {
        this.props.set_notification_variable(true, 'success', next ? 'Added to shortlist' : 'Removed from shortlist');
        this.setState((prev) => ({ detail: { ...prev.detail, is_shortlisted: next } }));
        this.patch_segment(selected_id, { is_shortlisted: next });
        // On the Short-listed tab, un-shortlisting removes the row + refreshes counts (keeping the
        // current category selected unless it just emptied).
        if (tab === 'shortlisted' && !next) this.load_categories(true);
      })
      .catch(this.show_error);
  };

  seek_to = (secs) => {
    const v = this.video_ref.current;
    if (!v) return;
    this.bounded_play = false; // clicking a transcript line is a manual seek, not a bounded play
    v.currentTime = secs;
    v.play();
  };

  scroll_active_cue = () => {
    const el = this.cue_refs[this.state.active_cue];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  };

  on_filter = (key) => (e) => this.setState({ [key]: e.target.value }, this.load_segments);

  on_query = (e) => this.setState({ query: e.target.value });

  submit_query = (e) => {
    if (e.key === 'Enter') this.load_segments();
  };

  clear_query = () => this.setState({ query: '' }, this.load_segments);

  save_rating = () => {
    const { selected_id, edit_rating } = this.state;
    const rating = Number(edit_rating);
    if (!rating || rating < 1 || rating > 10) {
      this.props.set_notification_variable(true, 'error', 'Rating must be an integer between 1 and 10');
      return;
    }
    put_api(get_api_url(`${BASE}/segments/${selected_id}/rating`), { rating }, true)
      .then(() => {
        this.props.set_notification_variable(true, 'success', 'Rating saved');
        this.patch_segment(selected_id, { rating });
        this.load_detail(selected_id);
      })
      .catch(this.show_error);
  };

  save_metadata = () => {
    const { selected_id, edit_title, edit_desc } = this.state;
    if (!edit_title.trim()) {
      this.props.set_notification_variable(true, 'error', 'Title is required');
      return;
    }
    put_api(get_api_url(`${BASE}/segments/${selected_id}/metadata`), { title: edit_title, description: edit_desc }, true)
      .then(() => {
        this.props.set_notification_variable(true, 'success', 'Saved');
        this.patch_segment(selected_id, { title: edit_title, description: edit_desc });
        this.load_detail(selected_id);
      })
      .catch(this.show_error);
  };

  save_notes = () => {
    const { selected_id, edit_notes } = this.state;
    put_api(get_api_url(`${BASE}/segments/${selected_id}/notes`), { reviewer_notes: edit_notes }, true)
      .then(() => this.props.set_notification_variable(true, 'success', 'Notes saved'))
      .catch(this.show_error);
  };

  play_segment = () => {
    const { detail } = this.state;
    const v = this.video_ref.current;
    if (!v || !detail) return;
    this.bounded_play = true; // stop at the segment end (feature: bounded Play segment)
    v.currentTime = detail.start_time_seconds || 0;
    v.play();
  };

  on_video_meta = () => {
    const { detail } = this.state;
    const v = this.video_ref.current;
    if (v && detail) v.currentTime = detail.start_time_seconds || 0;
  };

  on_video_time = () => {
    const { detail } = this.state;
    const v = this.video_ref.current;
    if (!v || !detail) return;
    // Bounded "Play segment": stop once past the segment end (leaves manual scrubbing alone).
    if (this.bounded_play && detail.end_time_seconds && v.currentTime >= detail.end_time_seconds) {
      v.pause();
      this.bounded_play = false;
    }
    // Highlight the transcript line currently being spoken (last cue that has started).
    const cues = detail.transcript_cues || [];
    if (cues.length) {
      const t = v.currentTime;
      let idx = -1;
      for (let i = 0; i < cues.length; i += 1) {
        if (cues[i].start_seconds <= t) idx = i;
        else break;
      }
      if (idx !== this.state.active_cue) {
        this.setState({ active_cue: idx }, this.scroll_active_cue);
      }
    }
  };

  export_csv = () => {
    const { segments } = this.state;
    const headers = ['session_uuid', 'teacher', 'student', 'rating', 'start', 'end', 'duration', 'type', 'title', 'description'];
    const rows = segments.map((s) =>
      [
        s.session_uuid,
        s.teacher_first_name,
        s.student_first_name,
        s.rating == null ? '' : s.rating,
        fmt_clock(s.start_time_seconds),
        fmt_clock(s.end_time_seconds),
        fmt_duration(s.duration_seconds),
        s.segment_type,
        s.title,
        s.description
      ]
        .map((v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`)
        .join(',')
    );
    const blob = new Blob([[headers.join(','), ...rows].join('\n')], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'yoga-cues.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  render_browse = () => {
    const { cat_id, title_id, expanded_id } = this.state;
    return (
      <div className="ycl-panel ycl-browse">
        <h3>Categories</h3>
        {this.visible_categories().map((cat) => {
          const open = cat.id === expanded_id;
          return (
            <div key={cat.id} className="ycl-cat-group">
              <button
                type="button"
                className={`ycl-cat ${cat.id === cat_id ? 'is-active' : ''} ${open ? 'is-open' : ''}`}
                aria-expanded={open}
                onClick={() => this.toggle_category(cat)}
              >
                <span className="ycl-arrow" aria-hidden="true">
                  ▸
                </span>
                <span className="ycl-cat-label">
                  <strong>{cat.name}</strong>
                  <span>
                    {plural(cat.segment_count, 'segment')} · {plural(cat.title_count, 'title')}
                  </span>
                </span>
              </button>
              <div className={`ycl-drill-wrap ${open ? 'is-open' : ''}`}>
                <div className="ycl-drill-inner">
                  {cat.titles && cat.titles.length > 0 && (
                    <div className="ycl-drill">
                      <button
                        type="button"
                        className={`ycl-title ${title_id === 0 ? 'is-active' : ''}`}
                        onClick={() => this.select_title(0)}
                      >
                        <strong>All {cat.name}</strong>
                      </button>
                      {cat.titles.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          className={`ycl-title ${title_id === t.id ? 'is-active' : ''}`}
                          onClick={() => this.select_title(t.id)}
                        >
                          <strong>{t.title}</strong>
                          <span>
                            {plural(t.session_count, 'session')}
                            {t.pose_family ? ` · ${t.pose_family}` : ''}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  render_row = (s) => {
    const { selected_id } = this.state;
    const names = [s.teacher_first_name, s.student_first_name].filter(Boolean).join(' - ');
    return (
      <tr
        key={s.id}
        role="button"
        tabIndex={0}
        className={s.id === selected_id ? 'is-selected' : ''}
        onClick={() => this.load_detail(s.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.load_detail(s.id);
          }
        }}
      >
        <td>
          <strong>{names || '—'}</strong>
        </td>
        <td>
          {fmt_clock(s.start_time_seconds)}
          <span className="ycl-muted"> ({fmt_duration(s.duration_seconds)})</span>
        </td>
        <td className="ycl-type-rating">
          <span className="ycl-pill">{s.segment_type}</span>
          {s.rating == null ? <span className="ycl-status">—</span> : <span className="ycl-score">{s.rating}</span>}
        </td>
        <td className="ycl-title-cell">
          <strong>{s.title}</strong>
          {s.confidence_score != null && s.confidence_score < LOW_CONFIDENCE && (
            <span className="ycl-pill warn"> low confidence</span>
          )}
        </td>
      </tr>
    );
  };

  render_card = (s) => {
    const { selected_id } = this.state;
    const names = [s.teacher_first_name, s.student_first_name].filter(Boolean).join(' - ');
    return (
      <div
        key={s.id}
        role="button"
        tabIndex={0}
        className={`ycl-card ${s.id === selected_id ? 'is-selected' : ''}`}
        onClick={() => this.load_detail(s.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.load_detail(s.id);
          }
        }}
      >
        <div className="ycl-card-title">{s.title}</div>
        {names && <div className="ycl-card-sub">{names}</div>}
        <div className="ycl-card-meta">
          <span className="ycl-pill">{s.segment_type}</span>
          <span className="ycl-score">{s.rating == null ? '—' : s.rating}</span>
          <span className="ycl-pill">
            {fmt_clock(s.start_time_seconds)} ({fmt_duration(s.duration_seconds)})
          </span>
          {s.confidence_score != null && s.confidence_score < LOW_CONFIDENCE && (
            <span className="ycl-pill warn">low confidence</span>
          )}
        </div>
      </div>
    );
  };

  render_detail = () => {
    const { detail, active_cue, edit_title, edit_desc, edit_notes, edit_rating } = this.state;
    if (!detail) {
      return (
        <div className="ycl-panel ycl-detail">
          <div className="ycl-empty">Select a segment to review it.</div>
        </div>
      );
    }
    const low_conf = detail.confidence_score != null && detail.confidence_score < LOW_CONFIDENCE;
    const hridaya_link = `/sessiondetails?id=${detail.session_uuid}`;
    return (
      <div className="ycl-panel ycl-detail">
        {detail.video_url ? (
          <video
            ref={this.video_ref}
            className="ycl-video"
            src={detail.video_url}
            controls
            onLoadedMetadata={this.on_video_meta}
            onTimeUpdate={this.on_video_time}
          >
            <track kind="captions" />
          </video>
        ) : (
          <div className="ycl-empty">No replay available.</div>
        )}
        <div className="ycl-actions">
          <button type="button" className="ycl-btn primary" onClick={this.play_segment}>
            Play segment
          </button>
          <a className="ycl-btn" href={hridaya_link} target="_blank" rel="noreferrer">
            Open in Hridaya
          </a>
          <button
            type="button"
            className={`ycl-btn ${detail.is_shortlisted ? 'shortlisted' : ''}`}
            onClick={this.toggle_shortlist}
          >
            {detail.is_shortlisted ? '★ Shortlisted' : '☆ Shortlist'}
          </button>
        </div>

        <div>
          <span className="ycl-pill">{detail.segment_type}</span>
          {low_conf && <span className="ycl-pill warn"> low confidence — inspect boundaries</span>}
        </div>

        <label className="ycl-field" htmlFor="ycl-title">
          Title
          <input
            id="ycl-title"
            value={edit_title}
            onChange={(e) => this.setState({ edit_title: e.target.value })}
          />
        </label>
        <label className="ycl-field" htmlFor="ycl-desc">
          Description
          <textarea
            id="ycl-desc"
            rows={2}
            value={edit_desc}
            onChange={(e) => this.setState({ edit_desc: e.target.value })}
          />
        </label>
        <button type="button" className="ycl-btn" onClick={this.save_metadata}>
          Save title / description
        </button>

        <dl className="ycl-meta">
          <div>
            <dt>Teacher</dt>
            <dd>{detail.teacher_name || '—'}</dd>
          </div>
          <div>
            <dt>Time</dt>
            <dd>
              {fmt_clock(detail.start_time_seconds)} – {fmt_clock(detail.end_time_seconds)}
            </dd>
          </div>
          <div>
            <dt>Duration</dt>
            <dd>{fmt_duration(detail.duration_seconds)}</dd>
          </div>
          <div>
            <dt>Pose family</dt>
            <dd>{detail.pose_family || '—'}</dd>
          </div>
        </dl>

        {detail.transcript_cues && detail.transcript_cues.length > 0 ? (
          <div className="ycl-timeline">
            {detail.transcript_cues.map((cue, i) => (
              <button
                type="button"
                key={`${cue.start_seconds}-${i}`}
                ref={(el) => {
                  this.cue_refs[i] = el;
                }}
                className={`ycl-cue ${i === active_cue ? 'is-active' : ''}`}
                onClick={() => this.seek_to(cue.start_seconds)}
              >
                <span className="ycl-cue-time">{fmt_clock(cue.start_seconds)}</span>
                <span className="ycl-cue-text">{cue.text}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="ycl-transcript">{detail.transcript || 'No transcript excerpt.'}</div>
        )}

        <label className="ycl-field" htmlFor="ycl-rating" style={{ marginTop: 12 }}>
          Rating (1–10)
          <input
            id="ycl-rating"
            type="number"
            min="1"
            max="10"
            step="1"
            value={edit_rating}
            onChange={(e) => this.setState({ edit_rating: e.target.value })}
          />
        </label>
        <button type="button" className="ycl-btn" onClick={this.save_rating}>
          Save rating
        </button>

        <label className="ycl-field" htmlFor="ycl-notes" style={{ marginTop: 12 }}>
          Reviewer notes
          <textarea
            id="ycl-notes"
            rows={2}
            value={edit_notes}
            onChange={(e) => this.setState({ edit_notes: e.target.value })}
          />
        </label>
        <button type="button" className="ycl-btn" onClick={this.save_notes}>
          Save notes
        </button>
      </div>
    );
  };

  render() {
    const { loading, segments, min_rating, query, load_error, tab } = this.state;
    const rows = this.sorted_segments();
    return (
      <CommonHeader title="Yoga cues library" loading={loading}>
        <div className="ycl-tabs">
          <button
            type="button"
            className={`ycl-tab ${tab === 'all' ? 'is-active' : ''}`}
            onClick={() => this.switch_tab('all')}
          >
            Entire list
          </button>
          <button
            type="button"
            className={`ycl-tab ${tab === 'shortlisted' ? 'is-active' : ''}`}
            onClick={() => this.switch_tab('shortlisted')}
          >
            Short-listed
          </button>
        </div>
        <div className="ycl-toolbar">
          <div className="ycl-search-wrap">
            <input
              className="ycl-search"
              placeholder="Search title, description, transcript, session…"
              value={query}
              onChange={this.on_query}
              onKeyDown={this.submit_query}
            />
            {query && (
              <button
                type="button"
                className="ycl-search-clear"
                onClick={this.clear_query}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
          <label htmlFor="ycl-minrating">
            Min rating
            <input
              id="ycl-minrating"
              type="number"
              min="0"
              max="10"
              value={min_rating}
              onChange={this.on_filter('min_rating')}
            />
          </label>
          <button type="button" className="ycl-csv" onClick={this.export_csv}>
            Export CSV
          </button>
        </div>

        <div className="ycl-workspace">
          {this.render_browse()}

          <div className="ycl-panel ycl-table-panel">
            <h3>{plural(segments.length, 'segment')}</h3>
            {load_error && (
              <div className="ycl-empty">
                Couldn’t load segments.{' '}
                <button type="button" className="ycl-btn" onClick={this.load_segments}>
                  Retry
                </button>
              </div>
            )}
            {!load_error && !loading && segments.length === 0 && (
              <div className="ycl-empty">No segments match these filters.</div>
            )}
            {!load_error && segments.length > 0 && (
              <>
                <div className="ycl-table-wrap">
                  <table className="ycl-table">
                    <thead>
                      <tr>
                        <th>Session</th>
                        <th className="ycl-sortable" onClick={() => this.set_sort('time')}>
                          Time{this.sort_indicator('time')}
                        </th>
                        <th className="ycl-sortable" onClick={() => this.set_sort('rating')}>
                          Type / Rating{this.sort_indicator('rating')}
                        </th>
                        <th className="ycl-sortable" onClick={() => this.set_sort('title')}>
                          Title{this.sort_indicator('title')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>{rows.map(this.render_row)}</tbody>
                  </table>
                </div>
                <div className="ycl-cards">{rows.map(this.render_card)}</div>
              </>
            )}
          </div>

          {this.render_detail()}
        </div>
      </CommonHeader>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) => dispatch(actions.set_notification_variable(show, type, mes))
});

export default connect(null, mapDispatchToProps)(YogaCuesLibrary);
